import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../services/apiService';
import CreditBalances from './CreditBalances';
import GiveBacks from './GiveBacks';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import { DSCard, DSPageHeader, DSSplitButton, DSInput, DSSelect, DSIcon, DSButton, DSTabs } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import { MembershipChip, PaymentMethodChip } from '../components/MembershipChip';
import NeedsAttention from './membership/NeedsAttention';
import MergePicker from './membership/MergePicker';
import MergeHistory from './membership/MergeHistory';
import MembershipPayments from './membership/MembershipPayments';
import { useUsers } from '../hooks/useUsers';
import { countMembers } from '../utils/membership';
import { phoneError, normalizePhone } from '../utils/phone';
import './Users.css';
import { formatDateOnly, dayOfMonthUTC } from '../utils/dates';

function formatShortDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function Users() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all'); // all, members, guests, onboarding, active, inactive, unpaid-periods
  const [sortField, setSortField] = useState('name'); // name, role, membership, status, balance
  const [sortDirection, setSortDirection] = useState('asc'); // asc, desc

  const {
    users,
    loading,
    error,
    unpaidUsers,
    onboardingUsers,
    createUser,
    saveRenewalDate: persistRenewalDate,
    saveMemberSince: persistMemberSince,
    getUnpaidMonthsForUser
  } = useUsers(filterType);

  // Top tab state. 'users', 'attention', 'payments' and 'reconcile' are the four
  // sections of this screen; 'credits' and 'givebacks' come from the bottom bar.
  //
  // Membership deliberately does NOT get its own destination. An admin looking
  // someone up and an admin clearing the work queue are the same person doing
  // adjacent jobs, and splitting them across two screens is what left the club
  // with seven places that each answered part of the question.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(
    searchParams.get('tab') === 'reconcile' ? 'merge' : 'users'
  );

  // Needs attention is a tab on THIS page, so a Merge button there navigates
  // to a URL this component is already mounted at. React Router updates the
  // search params without remounting, the useState initialiser above does not
  // run a second time, and the click appears to do nothing at all.
  useEffect(() => {
    if (searchParams.get('tab') === 'reconcile') setActiveTab('merge');
  }, [searchParams]);
  const [queueCount, setQueueCount] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  
  // Modal states
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  
  // Renewal date editing state
  const [editingRenewalDate, setEditingRenewalDate] = useState(null); // { userId, value }
  
  // Member since editing state
  const [editingMemberSince, setEditingMemberSince] = useState(null); // { userId, value }
  
  // Merge state (from MembershipManagement)
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeSearchResults, setMergeSearchResults] = useState([]);
  const [selectedSourceUser, setSelectedSourceUser] = useState(null);
  const [selectedTargetUser, setSelectedTargetUser] = useState(null);
  const [mergeSourceId, setMergeSourceId] = useState('');
  const [mergeTargetId, setMergeTargetId] = useState('');



  // Confirm modal state (replaces window.confirm())
  const [confirmState, setConfirmState] = useState(null);
  // Read by ConfirmModal. Nothing sets it any more: the only caller was the
  // merge, which now lives on its own route and tracks its own submitting
  // state. Left as a const rather than deleted because ConfirmModal still takes
  // the prop and other confirms may need it again.
  const [confirmSubmitting] = useState(false);

  // Form states
  const [newUser, setNewUser] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'member'
  });


  const handleMergeSearch = async (event) => {
    const term = event.target.value;
    setMergeSearch(term);
    if (term.length < 2) {
      setMergeSearchResults([]);
      return;
    }
    try {
      const res = await apiService.searchUsers(term);
      setMergeSearchResults(res.data);
    } catch (err) {
      // Searching is not the job. Say so and leave the previous results alone
      // rather than blanking the list and looking like nobody matched.
      showToast('Could not search for users', 'error');
    }
  };

  // Two slots, filled in the order they are picked. Which account survives is
  // NOT decided here - the merge screen owns that, with "Make primary" and a
  // sentence saying why - so this does not ask twice in worse words.
  const chooseMergeAccount = (user) => {
    const id = String(user.id);
    if (!selectedSourceUser) {
      setSelectedSourceUser(user);
      setMergeSourceId(id);
    } else if (!selectedTargetUser) {
      setSelectedTargetUser(user);
      setMergeTargetId(id);
    }
    setMergeSearch('');
    setMergeSearchResults([]);
  };

  const removeMergeAccount = (userId) => {
    if (selectedSourceUser && selectedSourceUser.id === userId) {
      // Promote the second pick so the empty slot is always the last one, and
      // picking again cannot silently land in the wrong place.
      setSelectedSourceUser(selectedTargetUser);
      setMergeSourceId(selectedTargetUser ? String(selectedTargetUser.id) : '');
      setSelectedTargetUser(null);
      setMergeTargetId('');
    } else if (selectedTargetUser && selectedTargetUser.id === userId) {
      setSelectedTargetUser(null);
      setMergeTargetId('');
    }
  };

  // Arriving with accounts already named in the URL.
  //
  // Both ids: nothing is left to pick, so hand straight over to the merge
  // review at its own URL. `replace` so the browser back button returns to
  // wherever the admin came from, not to this redirect.
  //
  // One id: the member panel's "merge this person" sends only a source, because
  // finding the other account is the whole job. Prefill that one and leave the
  // admin in the picker to search for the second.
  useEffect(() => {
    const source = searchParams.get('source');
    const target = searchParams.get('target');
    if (!source && !target) return;

    if (source && target) {
      navigate(`/users/merge?source=${source}&target=${target}`, { replace: true });
      return;
    }

    let cancelled = false;
    const only = source || target;
    setMergeSourceId(String(only));
    apiService.getUser(only)
      .then((res) => { if (!cancelled) setSelectedSourceUser(res.data); })
      .catch(() => { if (!cancelled) showToast('Could not load that account', 'error'); });
    return () => { cancelled = true; };
  }, [searchParams, navigate]);


  const resetMergeState = () => {
    setSelectedSourceUser(null);
    setSelectedTargetUser(null);
    setMergeSourceId('');
    setMergeTargetId('');
    setMergeSearch('');
    setMergeSearchResults([]);
  };


  const handleCreateUser = async () => {
    if (!newUser.firstName || !newUser.lastName) {
      showToast('First name and last name are required', 'error');
      return;
    }

    try {
      await createUser({ ...newUser, phone: normalizePhone(newUser.phone) });
      setShowAddUserModal(false);
      setNewUser({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'member'
      });
      showToast('User created successfully', 'success');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to create user', 'error');
      console.error(err);
    }
  };

  const getSmsHref = (phone) => {
    if (!phone) return null;
    const cleaned = phone.replace(/[^0-9+]/g, '');
    return cleaned ? `sms:${cleaned}` : null;
  };

  const getMailtoHref = (email) => {
    if (!email) return null;
    return `mailto:${email}`;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const startEditingRenewalDate = (userId, currentValue) => {
    // Convert day of month to a date input value (use current month/year)
    const today = new Date();
    const day = currentValue || 1;
    const dateValue = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setEditingRenewalDate({ userId, value: dateValue, dayOfMonth: currentValue });
  };

  const cancelEditingRenewalDate = () => {
    setEditingRenewalDate(null);
  };

  const saveRenewalDate = async () => {
    if (!editingRenewalDate) return;

    const { userId, value } = editingRenewalDate;
    
    // Extract day of month from the date
    // Read in UTC rather than nudged to noon. The noon trick was a local patch
    // for this same DATE-in-local-zone bug; `dayOfMonthUTC` is the shared fix.
    const dayOfMonth = dayOfMonthUTC(value);

    if (dayOfMonth < 1 || dayOfMonth > 31) {
      showToast('Please select a valid day of month (1-31)', 'error');
      return;
    }

    try {
      await persistRenewalDate(userId, dayOfMonth);
      setEditingRenewalDate(null);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update renewal date', 'error');
      console.error(err);
    }
  };

  const startEditingMemberSince = (userId, currentValue) => {
    // Format current value for date input
    let dateValue = '';
    if (currentValue) {
      const date = new Date(currentValue);
      dateValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    }
    setEditingMemberSince({ userId, value: dateValue });
  };

  const cancelEditingMemberSince = () => {
    setEditingMemberSince(null);
  };

  const saveMemberSince = async () => {
    if (!editingMemberSince) return;

    const { userId, value } = editingMemberSince;

    if (!value) {
      showToast('Please select a date', 'error');
      return;
    }

    try {
      await persistMemberSince(userId, value);
      setEditingMemberSince(null);
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to update member since date', 'error');
      console.error(err);
    }
  };

  // For onboarding filter, use the dedicated onboarding users list
  //
  // One shared rule, so this header, the Members tab and the cash picker cannot
  // drift apart again. The copy that used to live here counted merged-away
  // shells, which is why the club read as having 72 members rather than 70.
  const memberCount = countMembers(users);

  // Accounts, counted the way the list below counts them. Merged shells are
  // hidden from every view, so including them described a longer list than the
  // one on screen.
  const accountCount = users.filter((u) => !u.merged_into_user_id).length;
  const accountSummary = `${accountCount} accounts · ${memberCount} members`;

  // Credits and Give backs are separate screens that happen to share this
  // screen's bottom bar. They bring their own headers, so this one must not sit
  // above them.
  const isUserManagement = ['users', 'attention', 'payments', 'merge'].includes(activeTab);

  const displayUsers = filterType === 'onboarding' ? onboardingUsers : users;

  const filteredUsers = displayUsers.filter(user => {
    // Hide merged accounts from all views except 'inactive' filter
    if (user.merged_into_user_id && filterType !== 'inactive') {
      return false;
    }

    // Hide inactive users from all views except 'inactive' and 'onboarding' filters
    if (!user.is_active && filterType !== 'inactive' && filterType !== 'onboarding') {
      return false;
    }

    // Apply search filter
    if (searchTerm && searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = (
        user.email?.toLowerCase().includes(searchLower) ||
        user.first_name?.toLowerCase().includes(searchLower) ||
        user.last_name?.toLowerCase().includes(searchLower) ||
        user.preferred_name?.toLowerCase().includes(searchLower) ||
        user.stern_username?.toLowerCase().includes(searchLower) ||
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower)
      );
      if (!matchesSearch) return false;
    }

    // Apply type filter
    if (filterType === 'members') return user.is_member;
    if (filterType === 'unpaid-periods') {
      // Show only users that are in the unpaid list AND don't have auto-renewal
      return unpaidUsers.some(u => u.id === user.id) && !user.auto_renewal;
    }
    if (filterType === 'guests') return !user.is_member && user.membership_status !== 'pending';
    if (filterType === 'onboarding') return true; // Already filtered by using onboardingUsers
    if (filterType === 'active') return user.is_active;
    if (filterType === 'inactive') return !user.is_active;
    
    return true;
  }).sort((a, b) => {
    let aVal, bVal;
    
    switch (sortField) {
      case 'name':
        aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
        bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
        break;
      case 'role':
        aVal = a.role;
        bVal = b.role;
        break;
      case 'membership':
        // Sort order: member > onboarding > guest
        aVal = a.is_member ? 3 : (a.membership_status === 'pending' ? 2 : 1);
        bVal = b.is_member ? 3 : (b.membership_status === 'pending' ? 2 : 1);
        break;
      case 'status':
        aVal = a.is_active ? 1 : 0;
        bVal = b.is_active ? 1 : 0;
        break;
      case 'balance':
        aVal = parseFloat(a.credit_balance) || 0;
        bVal = parseFloat(b.credit_balance) || 0;
        break;
      default:
        return 0;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate stats
  const stats = {
    total: users.length,
    unpaidPeriods: unpaidUsers.filter(u => !u.auto_renewal).length,
    guests: users.filter(u => !u.is_member && u.membership_status !== 'pending').length,
    onboarding: onboardingUsers.length,
    active: users.filter(u => u.is_active).length,
    inactive: users.filter(u => !u.is_active).length
  };

  if (loading) {
    return <div className="page"><div className="loading">Loading users...</div></div>;
  }

  return (
    <div className="page users-page">
      {isUserManagement && (
        <>
      <DSPageHeader
        title="User Management"
        description={accountSummary}
        actions={
          <div className="desktop-header-actions">
            {/* Secondary, deliberately.
                Members create their own accounts: every one of them applies in
                the app before they reach Square. An admin has added an account
                8 times in the life of the club and not since March 2026, so
                this is the exception, not the way people join. It stays because
                it is the only way to make an account for somebody who cannot
                self-serve - two Square records carry no email address at all -
                but leading with it invites a second account for a person who
                already has one. See ACCOUNT_AND_MEMBERSHIP_LIFECYCLE.md. */}
            <DSButton tone="secondary" appearance="outline" onClick={() => setShowAddUserModal(true)}>
              Add user
            </DSButton>
          </div>
        }
      />

      {/* The four jobs, in the order an admin does them. Needs attention carries
          a count because it is the one number worth checking on the way past. */}
      <DSTabs
        items={[
          { key: 'users', label: 'Users' },
          { key: 'attention', label: 'Needs attention', count: queueCount || 0 },
          { key: 'payments', label: 'Payments' },
          { key: 'reconcile', label: 'Reconcile' }
        ]}
        activeKey={activeTab === 'merge' ? 'reconcile' : activeTab}
        onSelect={(key) => {
          if (activeTab === 'merge' && key !== 'reconcile') resetMergeState();
          setActiveTab(key === 'reconcile' ? 'merge' : key);
        }}
        ariaLabel="User management sections"
        className="users-top-tabs"
      />

      {error && <div className="users-alert error">{error}</div>}

        </>
      )}

      {activeTab === 'attention' && <NeedsAttention onCountChange={setQueueCount} />}
      {activeTab === 'payments' && <MembershipPayments />}

      {/* Users Tab Content */}
      {activeTab === 'users' && (
        <>

      {/* Search Bar */}
      <div className="users-search-container">
        <DSInput
          type="search"
          className="users-search-input"
          placeholder="Search name or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          leadingIcon={<DSIcon name="search" />}
        />
        <DSButton
          tone="secondary"
          appearance="outline"
          iconOnly
          className={filterType === 'all' ? '' : 'is-filtered'}
          aria-label="Filters"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((open) => !open)}
          leadingIcon={<DSIcon name="filter" />}
        />
      </div>

      {/* Filters. Kept behind a button rather than a chip strip: this is an
          occasional control, and the tabs above are the frequent one. */}
      {showFilters && (
        <div className="users-filters">
          {[
            { key: 'all', label: 'All', count: stats.total },
            { key: 'onboarding', label: 'Applications', count: stats.onboarding },
            { key: 'members', label: 'Members', count: memberCount },
            { key: 'unpaid-periods', label: 'Unpaid periods', count: stats.unpaidPeriods },
            { key: 'guests', label: 'Guests', count: stats.guests },
            { key: 'active', label: 'Active', count: stats.active },
            { key: 'inactive', label: 'Inactive', count: stats.inactive }
          ].map((option) => (
            <div
              key={option.key}
              className={`users-filter-chip ${filterType === option.key ? 'active' : ''}`}
              onClick={() => setFilterType(option.key)}
            >
              {option.label} ({option.count})
            </div>
          ))}
        </div>
      )}

      <p className="users-showing">
        Showing {filteredUsers.length} of {users.length}
      </p>

      {filteredUsers.length > 0 ? (
        <>
          {/* Desktop Table View */}
          <div className="table-responsive desktop-only">
            <div className="card">
              <table>
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Name {sortField === 'name' && (sortDirection === 'asc' ? <DSIcon name="chevronUp" /> : <DSIcon name="chevronDown" />)}
                    </th>
                    {filterType !== 'unpaid-periods' && filterType !== 'members' && (
                      <>
                        <th onClick={() => handleSort('role')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Role {sortField === 'role' && (sortDirection === 'asc' ? <DSIcon name="chevronUp" /> : <DSIcon name="chevronDown" />)}
                        </th>
                        <th onClick={() => handleSort('status')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                          Status {sortField === 'status' && (sortDirection === 'asc' ? <DSIcon name="chevronUp" /> : <DSIcon name="chevronDown" />)}
                        </th>
                      </>
                    )}
                    <th onClick={() => handleSort('membership')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Membership {sortField === 'membership' && (sortDirection === 'asc' ? <DSIcon name="chevronUp" /> : <DSIcon name="chevronDown" />)}
                    </th>
                    <th onClick={() => handleSort('balance')} style={{ cursor: 'pointer', userSelect: 'none' }}>
                      Balance {sortField === 'balance' && (sortDirection === 'asc' ? <DSIcon name="chevronUp" /> : <DSIcon name="chevronDown" />)}
                    </th>
                    {filterType !== 'unpaid-periods' && filterType !== 'members' && (
                      <th>Tab</th>
                    )}
                    {filterType === 'members' ? (
                      <>
                        <th>Member Since</th>
                        <th>Renewal Date</th>
                      </>
                    ) : filterType === 'unpaid-periods' ? (
                      <>
                        <th>Member Since</th>
                        <th>Unpaid Months</th>
                      </>
                    ) : (
                      <th>Application</th>
                    )}
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id}>
                      <td>
                        <div>
                          <strong>{user.first_name} {user.last_name}</strong>
                          <div style={{ fontSize: '0.85em', color: 'var(--ds-color-textMuted)' }}>{user.email}</div>
                          <div style={{ fontSize: '0.85em', color: user.phone_number ? 'var(--ds-color-textMuted)' : 'var(--ds-color-textDisabled)' }}>
                            {user.phone_number ? `${user.phone_number}`: ""}
                          </div>
                        </div>
                      </td>
                      {filterType !== 'unpaid-periods' && filterType !== 'members' && (
                        <>
                          <td><StatusBadge variant="neutral">{user.role}</StatusBadge></td>
                          <td>
                            <StatusBadge variant={user.is_active ? 'success' : 'error'}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </StatusBadge>
                          </td>
                        </>
                      )}
                      <td>
                        {/* Derived state, not the stored flag. The flag can be
                            wrong without looking wrong, which is the bug this
                            replaces. Falls back to the flag only for accounts
                            not yet computed. */}
                        <MembershipChip
                          status={user.status}
                          state={user.membership_state || (user.is_member ? 'ACTIVE' : 'NEVER')}
                          withIcon={false}
                        />
                        <PaymentMethodChip method={user.payment_method} state={user.membership_state} />
                      </td>
                      <td>
                        <strong style={{ color: user.credit_balance < 0 ? 'var(--ds-color-statusErrorText)' : 'inherit' }}>
                          ${user.credit_balance?.toFixed(2) || '0.00'}
                        </strong>
                      </td>
                      {filterType !== 'unpaid-periods' && filterType !== 'members' && (
                        <>
                          <td>
                            {user.tab_enabled ? (
                              <StatusBadge variant="info" leadingIcon={<DSIcon name="card" />}>{user.tab_limit === null ? <DSIcon name="infinity" /> : `$${parseFloat(user.tab_limit).toFixed(2)}`}
                              </StatusBadge>
                            ) : (
                              <span style={{ color: 'var(--ds-color-textDisabled)' }}> - </span>
                            )}
                          </td>
                          <td>
                            {user.has_application ? (
                              <StatusBadge variant="info" leadingIcon={<DSIcon name="clipboard" />}>Applied
                              </StatusBadge>
                            ) : (
                              <span style={{ color: 'var(--ds-color-textDisabled)' }}> - </span>
                            )}
                          </td>
                        </>
                      )}
                      {filterType === 'members' && (
                        <>
                          <td style={{ fontSize: '0.9em' }}>
                            {editingMemberSince?.userId === user.id ? (
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <DSInput
                                  type="date"
                                  value={editingMemberSince.value}
                                  onChange={(e) => setEditingMemberSince({ ...editingMemberSince, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveMemberSince();
                                    if (e.key === 'Escape') cancelEditingMemberSince();
                                  }}
                                  autoFocus
                                  style={{ padding: '4px', fontSize: '0.9em', width: '140px' }}
                                />
                                <button onClick={saveMemberSince} style={{ padding: '2px 6px', fontSize: '0.8em' }}><DSIcon name="check" /></button>
                                <button onClick={cancelEditingMemberSince} style={{ padding: '2px 6px', fontSize: '0.8em' }}><DSIcon name="x" /></button>
                              </div>
                            ) : (
                              <span 
                                onClick={() => startEditingMemberSince(user.id, user.start_date)}
                                style={{ cursor: 'pointer', borderBottom: '1px dashed var(--ds-color-borderSubtle)', padding: '4px' }}
                                title="Click to edit member since date"
                              >
                                {user.start_date ? (
                                  formatDateOnly(user.start_date)
                                ) : (
                                  <span style={{ color: 'var(--ds-color-statusErrorText)', fontWeight: 'bold' }}><DSIcon name="warning" /> Not set</span>
                                )}
                              </span>
                            )}
                          </td>
                          <td>
                            {editingRenewalDate?.userId === user.id ? (
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <DSInput
                                  type="date"
                                  value={editingRenewalDate.value}
                                  onChange={(e) => setEditingRenewalDate({ ...editingRenewalDate, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveRenewalDate();
                                    if (e.key === 'Escape') cancelEditingRenewalDate();
                                  }}
                                  autoFocus
                                  style={{ padding: '4px', fontSize: '0.9em', width: '140px' }}
                                />
                                <button onClick={saveRenewalDate} style={{ padding: '2px 6px', fontSize: '0.8em' }}><DSIcon name="check" /></button>
                                <button onClick={cancelEditingRenewalDate} style={{ padding: '2px 6px', fontSize: '0.8em' }}><DSIcon name="x" /></button>
                              </div>
                            ) : (
                              <span 
                                onClick={() => startEditingRenewalDate(user.id, user.renewal_day_of_month)}
                                style={{ cursor: 'pointer', borderBottom: '1px dashed var(--ds-color-borderSubtle)', padding: '4px' }}
                                title="Click to edit renewal date"
                              >
                                {user.renewal_day_of_month ? `Day ${user.renewal_day_of_month}` : 'Not set'}
                              </span>
                            )}
                          </td>
                        </>
                      )}
                      {filterType === 'unpaid-periods' && (
                        <>
                          <td style={{ fontSize: '0.9em' }}>
                            {editingMemberSince?.userId === user.id ? (
                              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                <DSInput
                                  type="date"
                                  value={editingMemberSince.value}
                                  onChange={(e) => setEditingMemberSince({ ...editingMemberSince, value: e.target.value })}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') saveMemberSince();
                                    if (e.key === 'Escape') cancelEditingMemberSince();
                                  }}
                                  autoFocus
                                  style={{ padding: '4px', fontSize: '0.9em', width: '140px' }}
                                />
                                <button onClick={saveMemberSince} style={{ padding: '2px 6px', fontSize: '0.8em' }}><DSIcon name="check" /></button>
                                <button onClick={cancelEditingMemberSince} style={{ padding: '2px 6px', fontSize: '0.8em' }}><DSIcon name="x" /></button>
                              </div>
                            ) : (
                              <span 
                                onClick={() => startEditingMemberSince(user.id, user.start_date)}
                                style={{ cursor: 'pointer', borderBottom: '1px dashed var(--ds-color-borderSubtle)', padding: '4px' }}
                                title="Click to edit member since date"
                              >
                                {user.start_date ? (
                                  formatDateOnly(user.start_date)
                                ) : (
                                  <span style={{ color: 'var(--ds-color-statusErrorText)', fontWeight: 'bold' }}><DSIcon name="warning" /> Not set</span>
                                )}
                              </span>
                            )}
                          </td>
                          <td style={{ fontSize: '0.9em', color: 'var(--ds-color-statusWarningText)' }}>
                            {getUnpaidMonthsForUser(user.id).join(', ')}
                          </td>
                        </>
                      )}
                      <td>
                        {filterType === 'unpaid-periods' ? (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <DSButton tone="primary" appearance="solid" size="sm" onClick={() => navigate(`/users/${user.id}?action=add-payment`)} title="Add payment for this user">
                              � Add Payment
                            </DSButton>
                            <DSSplitButton
                              label="Manage"
                              onMainClick={() => navigate(`/users/${user.id}`)}
                              tone="primary"
                              appearance="solid"
                              size="sm"
                              actions={[
                                { label: 'SMS User', onClick: () => { window.location.href = getSmsHref(user.phone_number); }, disabled: !getSmsHref(user.phone_number) },
                                { label: 'Email User', onClick: () => { window.location.href = getMailtoHref(user.email); }, disabled: !getMailtoHref(user.email) }
                              ]}
                            />
                          </div>
                        ) : (
                          <DSSplitButton
                            label="Manage"
                            onMainClick={() => navigate(`/users/${user.id}`)}
                            tone="primary"
                            appearance="solid"
                            size="sm"
                            actions={[
                              { label: 'SMS User', onClick: () => { window.location.href = getSmsHref(user.phone_number); }, disabled: !getSmsHref(user.phone_number) },
                              { label: 'Email User', onClick: () => { window.location.href = getMailtoHref(user.email); }, disabled: !getMailtoHref(user.email) }
                            ]}
                          />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Member list. Status, payment method and the reason behind the
              status all sit on the row, so scanning the list answers "is
              anything wrong" without opening anybody. */}
          <DSCard cornerVariant="lg" flush className="users-list">
            {filteredUsers.map(user => (
              <button
                type="button"
                key={user.id}
                className="users-row"
                onClick={() => navigate(`/users/${user.id}`)}
              >
                <span className="users-row__main">
                  <span className="users-row__name-line">
                    <span className="users-row__name">
                      {`${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                    </span>
                    <MembershipChip
                      status={user.status}
                      state={user.membership_state || (user.is_member ? 'ACTIVE' : 'NEVER')}
                      size="sm"
                      withIcon={false}
                    />
                    <PaymentMethodChip method={user.payment_method} state={user.membership_state} />
                  </span>

                  <span className="users-row__contact">
                    {user.email}
                    {user.phone_number ? ` · ${user.phone_number}` : ''}
                  </span>

                  {/* Never a status code. What happened, and when.
                      Only for people the club considers a member or is
                      onboarding: "Never held a membership" on four hundred guest
                      accounts is the same noise as the chip was. */}
                  {user.status && user.status.reason
                    && ['ACTIVE', 'AT_RISK', 'ONBOARDING'].includes(user.membership_state) && (
                    <span className="users-row__reason">
                      {user.status.reason}
                      {user.last_payment_date ? ` · last paid ${formatShortDate(user.last_payment_date)}` : ''}
                    </span>
                  )}

                  {filterType === 'unpaid-periods' && getUnpaidMonthsForUser(user.id).length > 0 && (
                    <span className="users-row__reason users-row__reason--warning">
                      Unpaid: {getUnpaidMonthsForUser(user.id).join(', ')}
                    </span>
                  )}
                </span>

                <span className="users-row__chevron" aria-hidden="true">
                  <DSIcon name="chevronRight" size="sm" />
                </span>
              </button>
            ))}
          </DSCard>
        </>
      ) : (
        <div className="card">
          <div className="users-empty-state">
            <div className="users-empty-state-icon"><DSIcon name="users" /></div>
            <h3>No Users Found</h3>
            <p>
              {searchTerm || filterType !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Get started by adding your first user'}
            </p>
            {!searchTerm && filterType === 'all' && (
              <DSButton tone="primary" appearance="solid" onClick={() => setShowAddUserModal(true)} style={{ fontSize: '16px', padding: '12px 24px' }}>
                + Add First User
              </DSButton>
            )}
          </div>
        </div>
      )}

        </>
      )}

      {/* Merge Tab Content */}
      {activeTab === 'merge' && (
        <MergePicker
          search={mergeSearch}
          results={mergeSearchResults}
          selected={[selectedSourceUser, selectedTargetUser].filter(Boolean)}
          onSearchChange={handleMergeSearch}
          onSelect={chooseMergeAccount}
          onRemove={removeMergeAccount}
          // The review fetches its own preview, so there is nothing to load
          // here - and a failure now shows on the screen that caused it rather
          // than as a toast over the picker.
          onContinue={() => navigate(`/users/merge?source=${mergeSourceId}&target=${mergeTargetId}`)}
          onBack={() => { setActiveTab('users'); resetMergeState(); }}
        />
      )}

      {/* The only view of account_merge_log in the app. A merge cannot be
          undone here, so this is how you find out what happened to somebody's
          other account. */}
      {activeTab === 'merge' && <MergeHistory />}

      {/* Credits Tab Content */}
      {activeTab === 'credits' && (
        <CreditBalances />
      )}

      {/* Give backs Tab Content */}
      {activeTab === 'givebacks' && (
        <GiveBacks />
      )}


      {/* Bottom tab bar - switches between the Users list, Credit Balances, and Give backs views */}
      <nav className="users-bottom-tabs">
        <button
          type="button"
          className={`users-bottom-tab-item ${['users', 'attention', 'payments', 'merge'].includes(activeTab) ? 'active' : ''}`}
          onClick={() => {
            if (activeTab === 'merge') {
              resetMergeState();
            }
            setActiveTab('users');
          }}
        >
          <span className="users-bottom-tab-icon"><DSIcon name="users" /></span>
          <span className="users-bottom-tab-label">Users</span>
        </button>
        <button
          type="button"
          className={`users-bottom-tab-item ${activeTab === 'credits' ? 'active' : ''}`}
          onClick={() => setActiveTab('credits')}
        >
          <span className="users-bottom-tab-icon"><DSIcon name="dollar" /></span>
          <span className="users-bottom-tab-label">Credits</span>
        </button>
        <button
          type="button"
          className={`users-bottom-tab-item ${activeTab === 'givebacks' ? 'active' : ''}`}
          onClick={() => setActiveTab('givebacks')}
        >
          <span className="users-bottom-tab-icon"><DSIcon name="heart" /></span>
          <span className="users-bottom-tab-label">Give backs</span>
        </button>
      </nav>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="users-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowAddUserModal(false)}>
          <div className="users-modal">
            <h2>Add New User</h2>
            
            <div className="users-form-group">
              <DSInput
                label="Email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                placeholder="Enter email address (optional)"
              />
            </div>

            <div className="users-form-group">
              <DSInput
                label="First Name *"
                type="text"
                value={newUser.firstName}
                onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                placeholder="Enter first name"
              />
            </div>

            <div className="users-form-group">
              <DSInput
                label="Last Name *"
                type="text"
                value={newUser.lastName}
                onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                placeholder="Enter last name"
              />
            </div>

            <div className="users-form-group">
              <DSInput
                label="Phone Number"
                type="tel"
                inputMode="tel"
                maxLength={20}
                value={newUser.phone}
                onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                state={phoneError(newUser.phone) ? 'error' : 'inactive'}
                helpText={phoneError(newUser.phone) || undefined}
                placeholder="Enter phone number (optional)"
              />
            </div>

            {/* Administrator is the only organisation-wide role. 'host' moved
                to a feature-role grant, and 'member'/'guest' never granted
                anything. See requirements section 14. */}
            <div className="users-form-group">
              <DSSelect
                label="Administrator"
                value={newUser.role === 'admin' ? 'admin' : 'member'}
                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                helpText="Full authority across the admin app. Membership is separate."
              >
                <option value="member">No</option>
                <option value="admin">Yes</option>
              </DSSelect>
            </div>

            <div className="users-modal-actions">
              <DSButton tone="primary" appearance="solid" onClick={handleCreateUser}>
                Create User
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={() => { setShowAddUserModal(false); setNewUser({ email: '', firstName: '', lastName: '', phone: '', role: 'member' }); }}>
                Cancel
              </DSButton>
            </div>
          </div>
        </div>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </div>
  );
}

export default Users;
